class GTMDataLayer extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-currency']
  }

  constructor () {
    super()

    // remove session storage item used for custom GTM conversion attribution
    sessionStorage.removeItem('BAO-add-to-cart-event-context')
    window.dataLayer = window.dataLayer || []
  }

  setupListeners () {
    super.setupListeners()

    this.trackCartChanged = this.trackCartChanged.bind(this)
    this.trackAddToCartForm = this.trackAddToCartForm.bind(this)
    this.trackRemoveFromCart = this.trackRemoveFromCart.bind(this)
    this.gtmPush = this.gtmPush.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.trackCartChanged,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.WISHLIST.UPDATED,
      this.trackWishlist.bind(this),
    )

    // Listen for data layer push events
    this.listeners.add(this, 'gtmPush', this.gtmPush)

    const desktopNavLinks = document.querySelectorAll(
      '[data-gtm-target-context="top-nav"] [data-gtm-nav-link]',
    )
    desktopNavLinks.forEach(el => {
      el.addEventListener('click', this.handleDesktopNavClick.bind(this))
    })

    const productCards = document.querySelectorAll('product-card')
    if (productCards.length) {
      productCards.forEach(el => {
        this.listeners.add(el, 'click', this.trackProductCardClick.bind(this))
      })
    }

    const nostoElements = document.querySelectorAll('.nosto_element')
    if (nostoElements.length) {
      const observer = new MutationObserver((mutations, observer) => {
        this.addListenersToNostoSections()
        observer.disconnect()
      })

      observer.observe(nostoElements[0], { childList: true, subtree: true })
    }

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.DRAWER.OPENED,
      event => {
        if (event.detail.activeDrawerKey !== 'menu') return

        const drawer = document.querySelector('site-drawer[key="menu"]')
        const drawerNavLinks = drawer.querySelectorAll('[data-gtm-nav-link]')

        drawerNavLinks.forEach(el => {
          el.addEventListener('click', this.handleDrawerNavClick.bind(this))
        })
      },
    )

    window.addEventListener('nostoRecs::quickBuy', this.trackNostoRecsQuickAdd)
  }

  gtmPush (evt) {
    const data = evt.detail
    window.dataLayer.push(data)
  }

  addListenersToNostoSections () {
    const nostoRecsCarousels = document.querySelectorAll(
      '.nosto_element carousel-element',
    )

    if (nostoRecsCarousels.length) {
      nostoRecsCarousels.forEach(carousel => {
        this.listeners.add(carousel, 'click', this.trackNostoRecsCarousels)
      })
    }

    const nostoProductCards = document.querySelectorAll(
      '.nosto_element product-card',
    )
    if (nostoProductCards.length) {
      nostoProductCards.forEach(el => {
        this.listeners.add(el, 'click', this.trackProductCardClick.bind(this))
      })
    }
  }

  trackNostoRecsQuickAdd (event) {
    const target = event.detail.event.target
    const targetContext = target.closest('[data-gtm-target-context]')

    if (!targetContext) return

    window.dataLayer.push({
      event: 'nosto_recs_interaction',
      target_context: targetContext.dataset.gtmTargetContext,
      event_action: 'Quick buy',
    })
  }

  trackNostoRecsCarousels (event) {
    const target = event.target
    const targetContext = target.closest('[data-gtm-target-context]')

    if (!targetContext) return

    const isArrowClick =
      target.closest('[data-carousel-element-el="previous"]') ||
      target.closest('[data-carousel-element-el="next"]')
    const isProductCardClick = target.closest('product-card')
    const isWishlistClick = target.closest(
      '[data-product-card-el="wishlistButton"]',
    )

    let eventAction = ''
    if (isArrowClick) {
      eventAction = 'Arrow click'
    } else if (isWishlistClick) {
      eventAction = 'Wishlist click'
    } else if (isProductCardClick) {
      eventAction = 'Product card click'
    } else {
      eventAction = 'Drag'
    }

    window.dataLayer.push({
      event: 'nosto_recs_interaction',
      target_context: targetContext.dataset.gtmTargetContext,
      event_action: eventAction,
    })
  }

  trackCartChanged (event) {
    const eventState = event.detail?.state

    if (!eventState) return

    const {
      items_removed: itemsRemoved,
      items_added: itemsAdded,
      items,
    } = eventState

    const cartAction = itemsRemoved?.length > 0 ? 'removed' : 'added'

    if (cartAction === 'removed') {
      this.trackRemoveFromCart(itemsRemoved)
    } else {
      const addedItems = itemsAdded || items
      const formName = this.getFormName(event.detail)
      const isPreOrder = event.detail.form.classList.contains(
        'x-pd-atc-form--preorder',
      )

      this.trackAddToCartForm(addedItems, formName, isPreOrder)
    }
  }

  /**
   * Check whether the submitted product form is a pushcart quantity adjustor or
   * a quickbuy form, otherwise grab the value from data-gtm-el
   *
   * @param {Object} eventDetail - the detail property from the custom PUSH_CART.CHANGED event,
   *    destructured to expose the form element and eventType property
   *
   * @returns {string} an identifier for the product form that the change event originated from
   */
  getFormName (eventDetail) {
    const { eventType, form } = eventDetail
    const formName =
      eventType === 'UPDATE_ITEM_QUANTITY'
        ? 'push-cart-quantity'
        : form?.dataset?.gtmTargetContext
    const isQuickBuyForm = formName === 'quickbuy-product-form'
    const quickBuyForm = sessionStorage.getItem('BAO-gtm-quick-buy-form')

    sessionStorage.removeItem('BAO-gtm-quick-buy-form')

    return isQuickBuyForm ? `quickbuy-${quickBuyForm}` : formName
  }

  /**
   * Format the data from each item in the items array and push to window.dataLayer
   * to track items added to the cart
   *
   * @param {Object[]} items - The item(s) that have been added to the cart
   * @param {string} formName - An identifier for the product form that the item(s) were added from
   */
  trackAddToCartForm (items, formName, isPreOrder) {
    items.forEach(item => {
      if (item.product_id.toString() === this.dataset.giftBoxId) return

      const titleSplit = item.title.split(' - ')
      const productTitle = item.untranslated_product_title || titleSplit[0]
      const variantTitle = item.untranslated_variant_title || titleSplit[1]
      const productPrice = item.original_line_price || item.price
      const eventContext = sessionStorage.getItem(
        'BAO-add-to-cart-event-context',
      )

      const eventAction = isPreOrder ? 'Pre-order' : 'Buy'

      window.dataLayer.push({
        event: 'add_to_cart_form',
        target_context: formName,
        event_context: eventContext || '',
        event_action: eventAction,
        item_title: productTitle,
        item_variant: variantTitle,
        item_discount: parseInt(item.total_discount) || 0.0,
        item_value: productPrice / 100,
      })

      if (!isPreOrder) return

      window.dataLayer.push({
        event: 'pre_order',
        target_context: formName,
        event_context: eventContext || '',
        item_title: productTitle,
        item_variant: variantTitle,
        item_value: productPrice / 100,
      })
    })
  }

  /**
   * Format the data from each item in the items array and push to window.dataLayer
   * to track items removed from the cart
   *
   * @param {Object[]} items - The item(s) that have been removed from the cart
   */
  trackRemoveFromCart (items) {
    const removedItems = []
    let removedItemsValue = 0

    items.forEach(item => {
      const productId = `${item.product_id}`

      if (productId === this.dataset.giftBoxId) return

      removedItemsValue += item.presentment_price

      removedItems.push({
        item_id: `${item.product_id}`,
        item_name: `${item.title}`,
        item_brand: `${item.vendor}`,
        item_variant: item.untranslated_variant_title
          ? `${item.untranslated_variant_title}`
          : '',
        price: item.presentment_price,
        quantity: item.quantity,
      })
    })

    if (!removedItems.length) return

    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: this.dataset.currency,
        value: removedItemsValue,
        items: removedItems,
      },
    })
  }

  handleDesktopNavClick (event) {
    event.stopImmediatePropagation()

    const target = event.currentTarget
    const targetContext = target.closest('[data-gtm-target-context]')
    const targetElement = target.closest('[data-gtm-target-element]')

    window.dataLayer.push({
      event: 'nav_link_click_desktop',
      target_text: target.dataset.gtmTargetText || target.innerText,
      target_context: targetContext?.dataset.gtmTargetContext,
      target_element: targetElement?.dataset.gtmTargetElement || 'link',
    })
  }

  handleDrawerNavClick (event) {
    event.stopImmediatePropagation()

    const target = event.currentTarget
    const targetLevel = target.closest('[data-gtm-target-level]')
    const targetContext = target.closest('[data-gtm-target-context]')
    const targetElement = target.closest('[data-gtm-target-element]')

    window.dataLayer.push({
      event: 'nav_link_click_drawer',
      target_text: target.dataset.gtmTargetText || target.innerText,
      target_level: targetLevel?.dataset.gtmTargetLevel,
      target_context: targetContext?.dataset.gtmTargetContext,
      target_element: targetElement?.dataset.gtmTargetElement || 'link',
    })
  }

  trackWishlist (event) {
    const detail = event.detail

    window.dataLayer.push({
      event: detail.eventType,
      ecommerce: {
        currency: this.dataset.currency,
        value: detail.productPrice,
        items: [
          {
            item_id: detail.productId,
            item_name: detail.productTitle,
          },
        ],
      },
    })
  }

  trackProductCardClick (event) {
    const target = event.target
    const wishlistTrigger = target.closest(
      '[data-product-card-el="wishlistButton"]',
    )
    const quickAddTrigger = target.closest(
      '[data-product-card-el="mobileTrigger"]',
    )
    const quickBuyContainer = target.closest(
      '[data-product-card-el="quickBuyContainer"]',
    )

    if (wishlistTrigger || quickAddTrigger || quickBuyContainer) return

    const card = event.currentTarget

    fetch(
      `${window.Shopify.routes.root}products/${card.dataset.productHandle}.js`,
    )
      .then(response => response.json())
      .then(product => {
        if (!product) return

        const itemListName =
          card.closest('[data-gtm-item-list-name]')?.dataset.gtmItemListName ||
          card.closest('[data-gtm-section]')?.dataset.gtmSection

        window.dataLayer.push({
          event: 'select_item',
          ecommerce: {
            item_list_name: itemListName || '',
            items: [
              {
                item_id: `${product.id}`,
                item_name: product.title,
                price: product.price / 100,
              },
            ],
          },
        })
      })
  }
}

if (!customElements.get('gtm-datalayer')) {
  customElements.define('gtm-datalayer', GTMDataLayer)
}
